<template>
  <div class="pb-tab">
    <PbSearchCard :exercises="personalBests" :mode="mode" @open-lift="openLift" />

    <div class="card">
      <div class="pb-head">
        <div>
          <h3>Personliga rekord per övning</h3>
          <p class="hint-text">Bästa vikten per antal reps (1–5). Tryck på ett rekord för detaljer, för att märka det som tävling eller för att plocka bort det så nästa räknas.</p>
        </div>
        <div class="pb-controls">
          <div class="seg">
            <button v-for="m in MODES" :key="m.key" :class="{ on: mode === m.key }" @click="setMode(m.key)">{{ m.icon }} {{ m.label }}</button>
          </div>
          <button class="fav-filter" :class="{ on: onlyFavorites }" @click="toggleOnlyFavorites" title="Visa bara favoritövningar">★ Bara favoriter</button>
        </div>
      </div>

      <div v-if="loading && !personalBests.length" class="empty-state">Hämtar rekord…</div>
      <div v-else-if="visibleBests.length === 0" class="empty-state">
        {{ onlyFavorites ? 'Inga favoritövningar än. Tryck på ☆ vid en övning så hamnar den överst.' : mode === 'competition' ? 'Inga tävlingslyft märkta än. Märk ett lyft som Tävling via ett rekord eller i Mina lyft.' : 'Inga lyft loggade än. Logga lyft under fliken "Logga lyft".' }}
      </div>

      <template v-else>
        <!-- Desktop: matristabell -->
        <div class="pb-scroll pb-desktop-only">
          <table class="pb-table">
            <thead>
              <tr>
                <th class="th-exercise">Övning</th>
                <th v-for="r in 5" :key="r" class="th-rep">{{ r }} rep{{ r > 1 ? 's' : '' }}</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="pb in visibleBests" :key="pb.exerciseTypeId" :class="{ fav: pb.isFavorite }">
                <td class="td-exercise">
                  <button class="fav-btn" :class="{ on: pb.isFavorite }" @click="toggleFavorite(pb)" :title="pb.isFavorite ? 'Ta bort favorit' : 'Gör till favorit (hamnar överst)'">{{ pb.isFavorite ? '★' : '☆' }}</button>
                  {{ pb.exerciseTypeName }}
                </td>
                <td v-for="r in 5" :key="r" class="td-rep">
                  <button v-if="getPbRecord(pb, r)" class="pb-cell" :class="{ comp: getPbRecord(pb, r).status === 'competition' }" @click="open(pb, getPbRecord(pb, r))" :title="cellTitle(getPbRecord(pb, r))">
                    <span class="pb-weight">{{ fmtKg(getPbRecord(pb, r).weight) }} kg <span v-if="getPbRecord(pb, r).status === 'competition'" class="pb-medal">🎖️</span></span>
                    <span class="pb-date">{{ formatDate(getPbRecord(pb, r).date) }}</span>
                  </button>
                  <span v-else class="pb-empty">—</span>
                </td>
              </tr>
            </tbody>
          </table>
        </div>

        <!-- Mobil: ett kort per övning med rep-nivåer -->
        <div class="pb-cards pb-mobile-only">
          <div v-for="pb in visibleBests" :key="pb.exerciseTypeId" class="pb-card" :class="{ fav: pb.isFavorite }">
            <div class="pb-card-name">
              <button class="fav-btn" :class="{ on: pb.isFavorite }" @click="toggleFavorite(pb)" :title="pb.isFavorite ? 'Ta bort favorit' : 'Gör till favorit (hamnar överst)'">{{ pb.isFavorite ? '★' : '☆' }}</button>
              {{ pb.exerciseTypeName }}
            </div>
            <div v-if="pbTiers(pb).length" class="pb-tier-grid">
              <button v-for="t in pbTiers(pb)" :key="t.reps" :class="['pb-tier', { top: t.reps === topReps(pb), comp: t.status === 'competition' }]" @click="open(pb, t)">
                <span class="pb-tier-reps">{{ t.reps }} rep{{ t.reps > 1 ? 's' : '' }}</span>
                <span class="pb-tier-weight">{{ fmtKg(t.weight) }}<small> kg</small></span>
                <span class="pb-tier-date">{{ t.status === 'competition' ? '🎖️ ' : '' }}{{ formatDate(t.date) }}</span>
              </button>
            </div>
            <div v-else class="pb-empty">Inga rekord på 1–5 reps än.</div>
          </div>
        </div>
      </template>
    </div>

    <LiftStatusSheet v-if="sheet" :context="sheet" :mode="mode" @close="sheet = null" @changed="onChanged" @open-session="$emit('open-session', $event)" />
  </div>
</template>

<script>
import { ref, computed } from 'vue';
import LiftStatusSheet from './LiftStatusSheet.vue';
import PbSearchCard from './PbSearchCard.vue';
import { gymAuthState, refreshIfNeeded } from '../../services/gymTrackerAuth.js';
import api from '../../services/gymTrackerApi.js';

const MODES = [
  { key: 'all', label: 'Alla giltiga', icon: '🏆' },
  { key: 'competition', label: 'Tävling', icon: '🎖️' },
  { key: 'training', label: 'Träning', icon: '🏋️' },
];

export default {
  name: 'GymPbTab',
  components: { LiftStatusSheet, PbSearchCard },
  emits: ['open-session', 'changed', 'favorites-changed'],
  setup(props, { emit, expose }) {
    const mode = ref(localStorage.getItem('gymtracker.pbMode') || 'all');
    const onlyFavorites = ref(localStorage.getItem('gymtracker.pbOnlyFav') === '1');
    const personalBests = ref([]);
    const visibleBests = computed(() => (onlyFavorites.value ? personalBests.value.filter(p => p.isFavorite) : personalBests.value));
    const loading = ref(false);
    const sheet = ref(null);

    async function withToken(fn) { await refreshIfNeeded(); return fn(gymAuthState.token); }

    async function reload() {
      loading.value = true;
      try {
        const d = await withToken(t => api.getPersonalBests(t, mode.value));
        personalBests.value = d.personalBests || [];
      } catch { /* toast hanteras av föräldern vid behov */ }
      finally { loading.value = false; }
    }
    function setMode(m) { mode.value = m; try { localStorage.setItem('gymtracker.pbMode', m); } catch { /* ignore */ } reload(); }
    function toggleOnlyFavorites() {
      onlyFavorites.value = !onlyFavorites.value;
      try { localStorage.setItem('gymtracker.pbOnlyFav', onlyFavorites.value ? '1' : '0'); } catch { /* ignore */ }
    }

    // Favoriter (samma FavoriteExercise som appens övningsväljare) ligger överst.
    // Optimistisk uppdatering + omsortering; servern sorterar likadant vid nästa laddning.
    function sortBests() {
      personalBests.value = [...personalBests.value].sort((a, b) => (b.isFavorite - a.isFavorite) || a.exerciseTypeName.localeCompare(b.exerciseTypeName, 'sv'));
    }
    async function toggleFavorite(pb) {
      const next = !pb.isFavorite;
      pb.isFavorite = next;
      sortBests();
      try {
        await withToken(t => next ? api.addFavoriteExercise(pb.exerciseTypeId, t) : api.removeFavoriteExercise(pb.exerciseTypeId, t));
        emit('favorites-changed');
      } catch {
        pb.isFavorite = !next;
        sortBests();
      }
    }

    function open(pb, rec) {
      sheet.value = { exerciseTypeId: pb.exerciseTypeId, exerciseTypeName: pb.exerciseTypeName, reps: rec.reps, liftId: rec.liftId };
    }
    // Träff i PB-sökningen → samma ark (detaljer + status + topplista).
    function openLift(ctx) { sheet.value = ctx; }
    async function onChanged() { await reload(); emit('changed'); }

    const getPbRecord = (pb, reps) => pb.repRecords.find(r => r.reps === reps) || null;
    function pbTiers(pb) {
      const out = [];
      for (let r = 1; r <= 5; r++) { const rec = pb.repRecords.find(x => x.reps === r); if (rec) out.push(rec); }
      return out;
    }
    function topReps(pb) {
      const tiers = pbTiers(pb);
      return tiers.length ? tiers.reduce((best, t) => (t.weight > best.weight ? t : best)).reps : null;
    }
    const formatDate = d => new Date(d).toLocaleDateString('sv-SE');
    const fmtKg = v => Number(v).toLocaleString('sv-SE', { maximumFractionDigits: 1 });
    const cellTitle = r => [r.sessionName ? `Pass: ${r.sessionName}` : 'Löst lyft', r.notes ? `📝 ${r.notes}` : '', r.statusNote ? `(${r.statusNote})` : ''].filter(Boolean).join(' · ');

    expose({ reload, setMode, open });
    return { MODES, mode, onlyFavorites, personalBests, visibleBests, loading, sheet, setMode, toggleOnlyFavorites, toggleFavorite, reload, open, openLift, onChanged, getPbRecord, pbTiers, topReps, formatDate, fmtKg, cellTitle };
  },
};
</script>

<style scoped>
.pb-tab { display: flex; flex-direction: column; gap: 1.25rem; }
.card { background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-lg); padding: 1.25rem; box-shadow: var(--gym-shadow-sm); }
.card h3 { margin: 0 0 0.35rem; font-size: 1.02rem; color: var(--gym-text); font-weight: 700; }
.hint-text { color: var(--gym-text-muted); font-size: 0.85rem; margin: 0; line-height: 1.55; }
.empty-state { color: var(--gym-text-muted); font-size: 0.9rem; padding: 1rem 0; }

.pb-head { display: flex; justify-content: space-between; gap: 1rem; align-items: flex-start; flex-wrap: wrap; margin-bottom: 1rem; padding-bottom: 0.8rem; border-bottom: 1px solid var(--gym-border); }
.pb-controls { display: flex; gap: 0.5rem; align-items: center; flex-wrap: wrap; }
.seg { display: inline-flex; background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-pill); padding: 3px; flex-shrink: 0; }
.fav-filter { border: 1px solid var(--gym-border); background: var(--gym-surface-2); color: var(--gym-text-muted); border-radius: var(--gym-radius-pill); padding: 0.4rem 0.85rem; font-weight: 700; font-size: 0.84rem; cursor: pointer; white-space: nowrap; }
.fav-filter.on { background: var(--gym-warning-soft); color: var(--gym-warning); border-color: var(--gym-warning); }
.fav-btn { background: none; border: none; cursor: pointer; font-size: 1.05rem; line-height: 1; padding: 0 0.3rem 0 0; color: var(--gym-text-faint); vertical-align: -1px; transition: color var(--gym-dur) var(--gym-ease), transform var(--gym-dur) var(--gym-ease-spring); }
.fav-btn:hover { color: var(--gym-warning); transform: scale(1.15); }
.fav-btn.on { color: var(--gym-warning); }
.pb-table tr.fav td { background: color-mix(in srgb, var(--gym-warning) 6%, transparent); }
.pb-card.fav { border-color: var(--gym-warning); }
.seg button { border: none; background: transparent; color: var(--gym-text-muted); padding: 0.4rem 0.85rem; border-radius: var(--gym-radius-pill); font-weight: 700; font-size: 0.84rem; cursor: pointer; white-space: nowrap; transition: background var(--gym-dur) var(--gym-ease), color var(--gym-dur) var(--gym-ease); }
.seg button.on { background: var(--gym-accent); color: var(--gym-on-accent); }

.pb-scroll { overflow-x: auto; border-radius: var(--gym-radius); }
.pb-table { width: 100%; border-collapse: collapse; font-size: 0.88rem; min-width: 540px; }
.pb-table th { text-align: left; padding: 0.6rem 0.75rem; background: var(--gym-surface-2); color: var(--gym-text-muted); font-size: 0.78rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; border-bottom: 1px solid var(--gym-border); }
.pb-table th.th-rep { text-align: center; }
.pb-table td { padding: 0.4rem 0.5rem; border-bottom: 1px solid var(--gym-border); vertical-align: middle; color: var(--gym-text); }
.pb-table tr:last-child td { border-bottom: none; }
.pb-table tr:hover td { background: var(--gym-surface-2); }
.td-exercise { font-weight: 600; padding-left: 0.75rem !important; }
.td-rep { text-align: center; }
.pb-cell { display: inline-flex; flex-direction: column; align-items: center; gap: 0.1rem; background: transparent; border: 1px solid transparent; border-radius: var(--gym-radius-sm); padding: 0.3rem 0.55rem; cursor: pointer; color: inherit; transition: background var(--gym-dur) var(--gym-ease), border-color var(--gym-dur) var(--gym-ease); }
.pb-cell:hover { background: var(--gym-accent-softer); border-color: var(--gym-accent-border); }
.pb-cell.comp { background: var(--gym-warning-soft); }
.pb-weight { font-weight: 700; color: var(--gym-accent); white-space: nowrap; }
.pb-medal { font-size: 0.85em; }
.pb-date { font-size: 0.75rem; color: var(--gym-text-faint); }
.pb-empty { color: var(--gym-text-faint); }

.pb-cards { display: flex; flex-direction: column; gap: 0.7rem; }
.pb-card { background: var(--gym-surface-2); border: 1px solid var(--gym-border); border-radius: var(--gym-radius); padding: 0.8rem 0.9rem; }
.pb-card-name { font-weight: 700; color: var(--gym-text); font-size: 0.98rem; margin-bottom: 0.6rem; }
.pb-tier-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(88px, 1fr)); gap: 0.45rem; }
.pb-tier { display: flex; flex-direction: column; align-items: flex-start; gap: 0.1rem; background: var(--gym-surface); border: 1px solid var(--gym-border); border-radius: var(--gym-radius-sm); padding: 0.5rem 0.55rem; cursor: pointer; color: inherit; text-align: left; }
.pb-tier:active { transform: scale(0.98); }
.pb-tier.top { background: var(--gym-accent-soft); border-color: var(--gym-accent-border); }
.pb-tier.comp { border-color: var(--gym-warning); }
.pb-tier-reps { font-size: 0.66rem; font-weight: 700; color: var(--gym-text-muted); text-transform: uppercase; letter-spacing: 0.03em; }
.pb-tier-weight { font-size: 1.02rem; font-weight: 800; color: var(--gym-accent); font-variant-numeric: tabular-nums; }
.pb-tier-weight small { font-size: 0.7rem; font-weight: 700; color: var(--gym-text-muted); }
.pb-tier-date { font-size: 0.68rem; color: var(--gym-text-faint); }

.pb-desktop-only { display: none; }
.pb-mobile-only { display: block; }
@media (min-width: 768px) {
  .pb-desktop-only { display: block; }
  .pb-mobile-only { display: none; }
}
</style>
